<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses, { 'sandbox-mode': isSandbox }]"
  >
    <div v-if="isSandbox" class="sandbox-banner" role="status">
      <strong>SANDBOX</strong> &middot; {{ sandboxMessage }}
    </div>

    <component :is="layout" v-cloak>
      <router-view :key="$route.path" />
    </component>

    <scroll-to-top v-if="enableScrollToTop" />

    <div id="sound-source" style="display: none"></div>
  </div>
</template>

<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { get } from "lodash"
import { watch } from '@vue/composition-api'
import { useCssVar, useWindowSize } from '@vueuse/core'
import { provideToast } from 'vue-toastification/composition'
import { MUTATE_ONLINE_USERS } from "@/store/config/mutation-keys"
import { $themeBreakpoints, $themeColors, $themeConfig } from '@themeConfig'

import store from '@/store'
import useAppConfig from '@core/app-config/useAppConfig'

const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')
const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')

export default {
  components: {
    // Layouts
    LayoutFull,
    LayoutVertical,
    LayoutHorizontal,

    ScrollToTop,
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  data(){
    return {
      loading: false,
      loaded: false
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      if (this.$route.meta.layout === 'horizontal') return 'layout-horizontal'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    settings() {
      return this.$store.getters[`auth/settings`];
    },
    isSandbox() {
      return !!get(this.settings, 'general_settings.sandbox_mode', false)
    },
    sandboxMessage() {
      return get(this.settings, 'general_settings.sandbox_banner_message') || 'Data here is for testing only.'
    },
    idle_timeout() {
      return get(this.settings, 'security_settings.idle_timeout', 5)
    }
  },
  watch: {
    isSandbox: {
      handler(isSandbox) {
        const base = document.title.replace(/^\[SANDBOX\]\s*/, '')
        document.title = isSandbox ? `[SANDBOX] ${base}` : base
      },
      immediate: true
    },
    "$socket.disconnected": {
      handler(disconnected){
        if (disconnected && this.isLoggedIn){
          this.retrySocketConnectionWithExponentialBackoff();
        }
      }, 
      deep: true,
      immediate: true
    },
    "$socket.connected": {
      handler(){
        // connected && console.log("[SOCKET CONNECTED]")
      }, 
      deep: true,
      immediate: true
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }


    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  destroyed() {
    this.$socket.client.close()
  },
  async created(){
    this.loaded = true;
    console.log("layout", this.layout)
  },
  methods: {
    updateOnlineUsers(onlineUsers){
      this.$store.commit(`auth/${MUTATE_ONLINE_USERS}`, onlineUsers);
    },
  },
}
</script>

<style scoped>
  [v-cloak] {
    display: none;
  }

  .sandbox-banner {
    position: sticky;
    top: 0;
    z-index: 2000;
    padding: 4px 12px;
    text-align: center;
    font-size: 13px;
    color: #fff;
    background: repeating-linear-gradient(45deg, #ea5455, #ea5455 12px, #d94344 12px, #d94344 24px);
  }
</style>

<style lang="scss">
// Sandbox: recolor every header/navbar variant so it is impossible to mistake for live.
#app.sandbox-mode {
  .header-navbar,
  .header-navbar.navbar-fixed,
  .header-navbar .navbar-container,
  .header-navbar.navbar-horizontal,
  .horizontal-menu-wrapper .header-navbar {
    background: #b02a2b !important;
    border-color: #8f2021 !important;
  }

  .header-navbar,
  .header-navbar .nav-link,
  .header-navbar .navbar-nav a,
  .header-navbar .dropdown-toggle,
  .header-navbar .user-name,
  .header-navbar .user-status,
  .header-navbar svg,
  .header-navbar .feather {
    color: #fff !important;
  }

  .header-navbar .navbar-nav .nav-link:hover {
    background: rgba(255, 255, 255, 0.15) !important;
  }

  // The white-text rules above also hit dropdown panels (white background), which made their
  // items invisible. Restore the theme colours inside any dropdown menu.
  .header-navbar .dropdown-menu,
  .horizontal-menu-wrapper .dropdown-menu {
    background: #fff !important;

    a,
    .dropdown-item,
    .dropdown-item span,
    .user-name,
    .user-status,
    svg,
    .feather {
      color: #6e6b7b !important;
    }

    .dropdown-item:hover,
    .dropdown-item:focus,
    a:hover {
      color: #7367f0 !important;
      background: rgba(115, 103, 240, 0.08) !important;

      svg,
      .feather,
      span {
        color: #7367f0 !important;
      }
    }

    .dropdown-item.active,
    .dropdown-item.active svg {
      color: #fff !important;
    }
  }
}
</style>
